import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MaterialModule } from '../../../material.module';
import { SpinnerComponent } from '../../../shared/spinner/spinner.component';
import { SpacerService, SpacerTestAttachmentDto, UpdateAttachmentDto } from '../../../services/spacer.service';
import { DeleteConfirmationDialogComponent } from '../../../projects-dashboard/well-attachments-dialog/delete-confirmation-dialog/delete-confirmation-dialog.component';


export interface SpacerTestAttachmentsDialogData {
  testInputMasterId: number;
  testType: string; // e.g., 'Spacer Compatibility', 'Spacer SSST', etc.
}

@Component({
  selector: 'app-spacer-test-attachments-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule, MatProgressSpinnerModule, SpinnerComponent],
  templateUrl: './spacer-test-attachments-dialog.component.html',
  styleUrls: ['./spacer-test-attachments-dialog.component.css']
})
export class SpacerTestAttachmentsDialogComponent implements OnInit {
  @ViewChild('fileUploadInput') fileUploadInput!: ElementRef<HTMLInputElement>;

  attachments: SpacerTestAttachmentDto[] = [];
  isLoading = false;
  errorMessage = '';
  selectedAttachmentForEdit: SpacerTestAttachmentDto | null = null;
  isEditPopupOpen = false;
  isUpdatingAttachment = false;
  editedDescription = '';

  // Upload properties
  isUploadPopupOpen = false;
  isUploading = false;
  directUploadError = '';
  uploadDescription = '';
  selectedFile: File | null = null;
  isDragOver = false;

  constructor(
    private readonly spacerService: SpacerService,
    private readonly dialog: MatDialog,
    private readonly dialogRef: MatDialogRef<SpacerTestAttachmentsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: SpacerTestAttachmentsDialogData
  ) { }

  ngOnInit(): void {
    this.loadAttachments();
  }

  loadAttachments(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.spacerService.getSpacerTestAttachmentsByTestInputMasterId(this.data.testInputMasterId).subscribe({
      next: (attachments) => {
        this.attachments = attachments.sort((left, right) => left.fileName.localeCompare(right.fileName));
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to load test attachments:', err);
        this.errorMessage = 'Failed to load attachments.';
        this.isLoading = false;
      }
    });
  }

  // Upload methods
  openUploadPopup(): void {
    this.isUploadPopupOpen = true;
    this.uploadDescription = '';
    this.selectedFile = null;
    this.directUploadError = '';
  }

  closeUploadPopup(): void {
    this.isUploadPopupOpen = false;
    this.uploadDescription = '';
    this.selectedFile = null;
    this.isUploading = false;
    this.isDragOver = false;
    this.directUploadError = '';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
    }
  }
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.selectedFile = files[0];
    }
  }

  uploadSelectedFile(): void {
    if (!this.selectedFile || this.isUploading) {
      return;
    }

    this.isUploading = true;
    this.directUploadError = '';

    this.spacerService.uploadSpacerTestAttachment(
      this.data.testInputMasterId ??0,
      this.selectedFile,
      this.uploadDescription
    ).subscribe({
      next: () => {
        this.closeUploadPopup();
        this.loadAttachments();
      },
      error: (err) => {
        console.error('Failed to upload attachment:', err);
        this.directUploadError = 'Failed to upload attachment. Please try again.';
        this.isUploading = false;
      }
    });
  }

  onDropOnTestSection(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.openUploadPopup();
      this.selectedFile = files[0];
    }
  }

  // Edit methods
  editAttachment(item: SpacerTestAttachmentDto): void {
    this.selectedAttachmentForEdit = item;
    this.editedDescription = item.description ?? '';
    this.isEditPopupOpen = true;
  }

  cancelEditAttachment(): void {
    this.isEditPopupOpen = false;
    this.isUpdatingAttachment = false;
    this.selectedAttachmentForEdit = null;
    this.editedDescription = '';
  }

  updateAttachmentDescription(): void {
    if (!this.selectedAttachmentForEdit || this.isUpdatingAttachment) {
      return;
    }
    const updateDto: UpdateAttachmentDto = {
      id: this.selectedAttachmentForEdit.id,
      fileName: this.selectedAttachmentForEdit.fileName,
      description: this.editedDescription ?? '',
      uploadedBy: this.selectedAttachmentForEdit.uploadedBy,
      ownerId: this.selectedAttachmentForEdit.ownerId
    };
    this.isUpdatingAttachment = true;
    this.spacerService.updateAttachment(updateDto).subscribe({
      next: () => {
        const targetId = this.selectedAttachmentForEdit?.id;
        this.attachments = this.attachments.map((a) =>
          a.id === targetId ? { ...a, description: this.editedDescription ?? '' } : a
        );
        this.cancelEditAttachment();
      },
      error: (err) => {
        console.error('Failed to update attachment description:', err);
        alert('Failed to update attachment. Please try again.');
        this.isUpdatingAttachment = false;
      }
    });
  }

  // Delete method
  deleteAttachment(attachment: SpacerTestAttachmentDto): void {
    const ref = this.dialog.open(DeleteConfirmationDialogComponent, {
      width: '520px',
      data: {
        title: 'Confirm Delete',
        message: `Delete "${attachment.fileName}"?`,
      }
    });

    ref.componentInstance.confirmRequested.subscribe(() => {
      ref.componentInstance.setDeleting(true);
      this.spacerService.deleteAttachment(attachment.id).subscribe({
        next: () => {
          this.attachments = this.attachments.filter(a => a.id !== attachment.id);
          ref.close(true);
        },
        error: (err) => {
          console.error('Failed to delete test attachment:', err);
          this.errorMessage = 'Failed to delete attachment.';
          ref.componentInstance.setDeleting(false);
        }
      });
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  downloadAttachment(attachment: SpacerTestAttachmentDto): void {
    this.spacerService.downloadSpacerTestAttachment(attachment.ownerId, attachment.id).subscribe({
      next: (blob) => {
        const fileUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = attachment.fileName;
        link.click();
        URL.revokeObjectURL(fileUrl);
      },
      error: (err) => {
        console.error('Failed to download test attachment:', err);
        this.errorMessage = 'Failed to download attachment.';
      }
    });
  }
}
